import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, UtensilsCrossed, Inbox, Salad, Sparkles, Settings as SettingsIcon,
  Plus, Pencil, Trash2, Upload, Loader2, X, ExternalLink, PartyPopper,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import api, { formatApiError } from "@/lib/api";
import { uploadToCloudinary } from "@/lib/cloudinary";
import DishImage from "@/components/DishImage";
import FestivalsTab from "@/pages/FestivalsTab";

const input = "w-full bg-[#2e2e2e] border border-white/25 rounded-sm px-3 py-2.5 text-white text-sm placeholder:text-white/50 outline-none focus:border-[#C9A66B] focus:ring-1 focus:ring-[#C9A66B]/60 transition-colors";
const label = "block mb-1.5 text-xs font-semibold tracking-wide text-[#E4CE9E]";

const STATUSES = ["New", "Contacted", "Closed"];
const STATUS_COLOR = { New: "bg-[#C9A66B] text-[#1A1A1A]", Contacted: "bg-blue-500/20 text-blue-300", Closed: "bg-white/10 text-white/60" };

/* -------------------------------------------------- Enquiries */
function EnquiriesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/enquiries").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load enquiries")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    try { await api.patch(`/admin/enquiries/${id}`, { status }); toast.success("Status updated"); }
    catch { toast.error("Update failed"); load(); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <div className="py-20 text-center text-[#C9A66B]"><Loader2 className="animate-spin inline" /></div>;
  if (!rows.length) return <p className="text-white/50 py-16 text-center">No enquiries yet.</p>;

  return (
    <div className="space-y-3" data-testid="enquiries-tab">
      {rows.map((r) => (
        <div key={r.id} className="bg-[#232323] border border-white/20 rounded-sm p-5" data-testid={`enquiry-row-${r.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-3">
                <h4 className="text-white font-medium text-lg">{r.name}</h4>
                <span className={`text-[0.65rem] px-2 py-0.5 rounded-sm font-semibold ${STATUS_COLOR[r.status] || ""}`}>{r.status}</span>
              </div>
              <p className="text-white/50 text-sm mt-1">{r.phone}{r.email ? ` · ${r.email}` : ""}</p>
              <p className="text-white/40 text-xs mt-1">
                {r.occasion || "—"} · {r.guest_count || 0} guests {r.event_date ? `· ${r.event_date}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`enquiry-status-${r.id}`}
                className="bg-[#1A1A1A] border border-white/20 rounded-sm px-2 py-1.5 text-white text-xs outline-none focus:border-[#C9A66B]">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              {r.menu && r.menu.dishes?.length ? (
                <button onClick={() => setOpen(r)} className="border border-[#C9A66B] text-[#C9A66B] hover:bg-[#C9A66B] hover:text-[#1A1A1A] transition-colors text-xs px-3 py-1.5 rounded-sm flex items-center gap-1" data-testid={`enquiry-view-menu-${r.id}`}>
                  <ExternalLink size={12} /> Menu ({r.menu.dishes.length})
                </button>
              ) : null}
              <button onClick={() => del(r.id)} data-testid={`enquiry-delete-${r.id}`}
                className="border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors text-xs px-3 py-1.5 rounded-sm flex items-center gap-1">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
          {r.message ? <p className="text-white/60 text-sm mt-3 border-t border-white/5 pt-3">{r.message}</p> : null}
        </div>
      ))}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-5" onClick={() => setOpen(null)}>
          <div className="bg-[#232323] border border-white/20 rounded-sm p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white serif text-2xl">Attached Menu</h3>
              <button onClick={() => setOpen(null)} className="text-white/60 hover:text-white"><X /></button>
            </div>
            <p className="text-white/50 text-sm mb-4">{open.menu.occasion} · {open.menu.guest_count} guests · ₹{open.menu.per_plate_estimate}/plate</p>
            <div className="space-y-2">
              {open.menu.dishes.map((d) => (
                <div key={d.id} className="flex items-center gap-3 text-white/80 text-sm border-b border-white/5 pb-2">
                  <div className="w-9 h-9 rounded-sm overflow-hidden shrink-0"><DishImage src={d.image} alt={d.name} className="w-full h-full object-cover" /></div>
                  {d.name} <span className="ml-auto text-white/40 text-xs">{d.category}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------- Dish dialog */
function DishDialog({ dish, categories, cloudinaryEnabled, onClose, onSaved }) {
  const empty = { name: "", category: categories[0] || "", price: 0, description: "", desc_mr: "", image: "", dietary: [], featured: false, active: true };
  const [form, setForm] = useState(dish ? { ...empty, ...dish } : empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDiet = (d) => set("dietary", form.dietary.includes(d) ? form.dietary.filter((x) => x !== d) : [...form.dietary, d]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const url = await uploadToCloudinary(file); set("image", url); toast.success("Image uploaded"); }
    catch (err) { toast.error(err.message || "Upload failed. Paste an image URL instead."); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Dish name required"); return; }
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (dish) await api.put(`/admin/dishes/${dish.id}`, payload);
      else await api.post("/admin/dishes", payload);
      toast.success(dish ? "Dish updated" : "Dish added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-5" onClick={onClose}>
      <div className="bg-[#232323] border border-white/20 rounded-sm p-6 max-w-lg w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dish-dialog">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-white serif text-2xl">{dish ? "Edit Dish" : "Add Dish"}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X /></button>
        </div>
        <div className="space-y-4">
          <div><label className={label}>Name</label><input className={input} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="dish-name-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={label}>Category</label>
              <select className={input} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="dish-category-input">
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><label className={label}>Price (₹ / plate)</label><input type="number" className={input} value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="dish-price-input" /></div>
          </div>
          <div><label className={label}>Description (English)</label><textarea className={input} rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div><label className={label}>Marathi Description (मराठी)</label><textarea className={input} rows={2} value={form.desc_mr || ""} onChange={(e) => set("desc_mr", e.target.value)} data-testid="dish-desc-mr" /></div>

          <div>
            <label className={label}>Image</label>
            <div className="flex gap-3 items-center">
              {form.image ? <img src={form.image} alt="" className="w-16 h-16 object-cover rounded-sm border border-white/20" /> : null}
              <div className="flex-1 space-y-2">
                {cloudinaryEnabled && (
                  <label className="border border-[#C9A66B] text-[#C9A66B] hover:bg-[#C9A66B] hover:text-[#1A1A1A] transition-colors text-xs px-3 py-2 rounded-sm flex items-center gap-2 cursor-pointer w-fit" data-testid="dish-image-upload">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
                    <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
                  </label>
                )}
                <input className={input} placeholder="or paste image URL" value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="dish-image-url" />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex gap-2">
              {["veg", "jain"].map((d) => (
                <button key={d} onClick={() => toggleDiet(d)} className={`text-xs px-3 py-1.5 rounded-sm border capitalize ${form.dietary.includes(d) ? "bg-[#C9A66B] border-[#C9A66B] text-[#1A1A1A]" : "border-white/20 text-white/60"}`}>{d}</button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-white/85 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
            <label className="flex items-center gap-2 text-white/85 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Active</label>
          </div>

          <button onClick={save} disabled={saving} className="btn-gold w-full py-3 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="dish-save-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null} Save Dish
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Dishes */
function DishesTab({ categories, cloudinaryEnabled }) {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit

  const load = useCallback(() => {
    setLoading(true);
    api.get("/dishes?include_inactive=true").then((r) => setDishes(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this dish?")) return;
    try { await api.delete(`/admin/dishes/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="dishes-tab">
      <div className="flex justify-between items-center mb-5">
        <p className="text-white/50 text-sm">{dishes.length} dishes</p>
        <button onClick={() => setEditing(null)} className="btn-gold px-4 py-2.5 rounded-sm text-sm flex items-center gap-2" data-testid="add-dish-btn"><Plus size={16} /> Add Dish</button>
      </div>
      {loading ? <div className="py-20 text-center text-[#C9A66B]"><Loader2 className="animate-spin inline" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {dishes.map((d) => (
            <div key={d.id} className="bg-[#232323] border border-white/20 rounded-sm overflow-hidden" data-testid={`admin-dish-${d.id}`}>
              <div className="h-32 overflow-hidden relative">
                <DishImage src={d.image} alt={d.name} className="w-full h-full object-cover" />
                {!d.active && <span className="absolute top-2 left-2 bg-black/70 text-white/85 text-[0.6rem] px-2 py-0.5 rounded-sm">Inactive</span>}
                {d.featured && <span className="absolute top-2 right-2 bg-[#C9A66B] text-[#1A1A1A] text-[0.6rem] px-2 py-0.5 rounded-sm font-semibold">Featured</span>}
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div><h4 className="text-white font-medium">{d.name}</h4><p className="text-white/40 text-xs">{d.category} · ₹{d.price}</p></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditing(d)} className="border border-[#C9A66B] text-[#C9A66B] hover:bg-[#C9A66B] hover:text-[#1A1A1A] transition-colors flex-1 py-2 rounded-sm text-xs flex items-center justify-center gap-1" data-testid={`edit-dish-${d.id}`}><Pencil size={12} /> Edit</button>
                  <button onClick={() => del(d.id)} className="border border-red-500/30 text-red-400 px-3 py-2 rounded-sm text-xs hover:bg-red-500/10" data-testid={`delete-dish-${d.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing !== undefined && (
        <DishDialog dish={editing} categories={categories} cloudinaryEnabled={cloudinaryEnabled}
          onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}

/* -------------------------------------------------- Presets */
function PresetsTab({ categories }) {
  const [presets, setPresets] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [editing, setEditing] = useState(undefined);

  const load = useCallback(() => {
    Promise.all([api.get("/presets"), api.get("/dishes")]).then(([p, d]) => { setPresets(p.data); setDishes(d.data); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { if (!window.confirm("Delete preset?")) return; await api.delete(`/admin/presets/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="presets-tab">
      <div className="flex justify-between items-center mb-5">
        <p className="text-white/50 text-sm">{presets.length} presets</p>
        <button onClick={() => setEditing(null)} className="btn-gold px-4 py-2.5 rounded-sm text-sm flex items-center gap-2" data-testid="add-preset-btn"><Plus size={16} /> Add Preset</button>
      </div>
      <div className="space-y-3">
        {presets.map((p) => (
          <div key={p.id} className="bg-[#232323] border border-white/20 rounded-sm p-5 flex justify-between items-center" data-testid={`preset-row-${p.id}`}>
            <div><h4 className="text-white font-medium">{p.name}</h4><p className="text-white/40 text-xs mt-1">{p.occasion} · {p.dish_ids.length} dishes</p></div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(p)} className="border border-[#C9A66B] text-[#C9A66B] hover:bg-[#C9A66B] hover:text-[#1A1A1A] transition-colors px-3 py-2 rounded-sm text-xs flex items-center gap-1"><Pencil size={12} /> Edit</button>
              <button onClick={() => del(p.id)} className="border border-red-500/30 text-red-400 px-3 py-2 rounded-sm text-xs hover:bg-red-500/10"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing !== undefined && <PresetDialog preset={editing} dishes={dishes} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}

function PresetDialog({ preset, dishes, onClose, onSaved }) {
  const empty = { name: "", occasion: "Wedding", description: "", dish_ids: [] };
  const [form, setForm] = useState(preset ? { ...empty, ...preset } : empty);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setForm((f) => ({ ...f, dish_ids: f.dish_ids.includes(id) ? f.dish_ids.filter((x) => x !== id) : [...f.dish_ids, id] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (preset) await api.put(`/admin/presets/${preset.id}`, form); else await api.post("/admin/presets", form);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-5" onClick={onClose}>
      <div className="bg-[#232323] border border-white/20 rounded-sm p-6 max-w-lg w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="preset-dialog">
        <div className="flex justify-between items-center mb-5"><h3 className="text-white serif text-2xl">{preset ? "Edit Preset" : "Add Preset"}</h3><button onClick={onClose} className="text-white/60"><X /></button></div>
        <div className="space-y-4">
          <div><label className={label}>Name</label><input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="preset-name-input" /></div>
          <div><label className={label}>Occasion</label>
            <select className={input} value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })}>
              {["Wedding", "Corporate", "Birthday", "Private Party"].map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div><label className={label}>Description</label><input className={input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div>
            <label className={label}>Dishes ({form.dish_ids.length})</label>
            <div className="max-h-56 overflow-y-auto border border-white/20 rounded-sm p-2 space-y-1">
              {dishes.map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-white/85 text-sm px-2 py-1 hover:bg-white/5 rounded-sm cursor-pointer">
                  <input type="checkbox" checked={form.dish_ids.includes(d.id)} onChange={() => toggle(d.id)} /> {d.name} <span className="text-white/30 text-xs ml-auto">{d.category}</span>
                </label>
              ))}
            </div>
          </div>
          <button onClick={save} disabled={saving} className="btn-gold w-full py-3 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="preset-save-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : null} Save Preset</button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Settings */
function SettingsTab({ cloudinaryEnabled }) {
  const { settings, refresh } = useSite();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  useEffect(() => { if (settings) setForm({ ...settings, categories: settings.categories || [], category_limits: settings.category_limits || {} }); }, [settings]);
  if (!form) return <div className="py-20 text-center text-[#C9A66B]"><Loader2 className="animate-spin inline" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLimit = (cat, v) => setForm((f) => ({ ...f, category_limits: { ...f.category_limits, [cat]: parseInt(v) || 0 } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: form.business_name, tagline: form.tagline, phone: form.phone, whatsapp: form.whatsapp,
        email: form.email, address: form.address, base_price_per_plate: parseFloat(form.base_price_per_plate) || 0,
        categories: form.categories, category_limits: form.category_limits, logo_url: form.logo_url || "",
        festival_enabled: !!form.festival_enabled, festival_message: form.festival_message || "",
      };
      await api.put("/admin/settings", payload);
      toast.success("Settings saved"); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="settings-tab">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={label}>Business Name</label><input className={input} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="settings-business-name" /></div>
        <div><label className={label}>Tagline</label><input className={input} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} /></div>
        <div><label className={label}>Phone</label><input className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="settings-phone" /></div>
        <div><label className={label}>WhatsApp (digits, e.g. 9198...)</label><input className={input} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></div>
        <div><label className={label}>Email</label><input className={input} value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
        <div><label className={label}>Base price / plate (₹)</label><input type="number" className={input} value={form.base_price_per_plate} onChange={(e) => set("base_price_per_plate", e.target.value)} /></div>
      </div>
      <div><label className={label}>Address</label><textarea className={input} rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="settings-address" /></div>

      <div>
        <label className={label}>Logo (shown in navbar & footer)</label>
        <div className="flex gap-3 items-center">
          {form.logo_url ? <img src={form.logo_url} alt="logo preview" className="h-14 w-auto object-contain bg-white/5 rounded-sm p-1" /> : null}
          <div className="flex-1 space-y-2">
            {cloudinaryEnabled && (
              <label className="border border-[#C9A66B] text-[#C9A66B] hover:bg-[#C9A66B] hover:text-[#1A1A1A] transition-colors text-xs px-3 py-2 rounded-sm flex items-center gap-2 cursor-pointer w-fit" data-testid="settings-logo-upload">
                {uploadingLogo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload Logo
                <input type="file" accept="image/*" className="hidden" disabled={uploadingLogo}
                  onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    setUploadingLogo(true);
                    try { const url = await uploadToCloudinary(file); set("logo_url", url); toast.success("Logo uploaded — click Save Settings"); }
                    catch (err) { toast.error(err.message || "Upload failed. Paste a URL instead."); }
                    finally { setUploadingLogo(false); }
                  }} />
              </label>
            )}
            <input className={input} placeholder="or paste logo URL (e.g. /assets/logo.png)" value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} data-testid="settings-logo-url" />
          </div>
        </div>
      </div>

      <div>
        <label className={label}>Category limits (max dishes per category)</label>
        <div className="grid sm:grid-cols-2 gap-3">
          {form.categories.map((c) => (
            <div key={c} className="flex items-center gap-3 bg-[#232323] border border-white/20 rounded-sm px-3 py-2">
              <span className="text-white/85 text-sm flex-1">{c}</span>
              <input type="number" min={0} className="w-16 bg-[#1A1A1A] border border-white/20 rounded-sm px-2 py-1 text-white text-sm outline-none focus:border-[#C9A66B]"
                value={form.category_limits[c] ?? ""} onChange={(e) => setLimit(c, e.target.value)} data-testid={`limit-${c.replace(/[^a-zA-Z]/g, "-")}`} />
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="btn-gold px-8 py-3 rounded-sm flex items-center gap-2 disabled:opacity-60" data-testid="settings-save-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : null} Save Settings</button>
    </div>
  );
}

/* -------------------------------------------------- Dashboard shell */
const TABS = [
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "dishes", label: "Dishes", icon: Salad },
  { key: "presets", label: "Presets", icon: Sparkles },
  { key: "festivals", label: "Festivals", icon: PartyPopper },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { logout } = useAuth();
  const { settings } = useSite();
  const nav = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [cloudinaryEnabled, setCloudinaryEnabled] = useState(false);

  useEffect(() => { api.get("/admin/config").then((r) => setCloudinaryEnabled(r.data.cloudinary_enabled)).catch(() => {}); }, []);

  const categories = settings?.categories || [];

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="admin-dashboard">
      <header className="border-b border-white/20 sticky top-0 bg-[#1A1A1A] z-40">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 flex items-center justify-center bg-[#C9A66B]"><UtensilsCrossed size={18} className="text-[#1A1A1A]" /></span>
            <div><p className="text-white serif text-lg leading-none">Mayuresh Admin</p><p className="text-white/40 text-xs mt-0.5">Owner Panel</p></div>
          </div>
          <button onClick={doLogout} className="text-white/60 hover:text-white flex items-center gap-2 text-sm" data-testid="admin-logout-btn"><LogOut size={16} /> Logout</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`px-4 py-2.5 rounded-sm text-sm flex items-center gap-2 whitespace-nowrap transition-colors ${tab === t.key ? "bg-[#C9A66B] text-[#1A1A1A]" : "text-white/60 hover:bg-white/5"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && <EnquiriesTab />}
        {tab === "dishes" && <DishesTab categories={categories} cloudinaryEnabled={cloudinaryEnabled} />}
        {tab === "presets" && <PresetsTab categories={categories} />}
        {tab === "festivals" && <FestivalsTab cloudinaryEnabled={cloudinaryEnabled} />}
        {tab === "settings" && <SettingsTab cloudinaryEnabled={cloudinaryEnabled} />}
      </div>
    </div>
  );
}
